/**
 * @copyright (C) 2023, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Heeseok Shin (hsshin@rsupport.com)
 *
 * @description
 *
 * @created at Thu Dec 21 2023
 **/

import { useStompClient, useStompManager } from "@/frameworks/libraries/network";
import { useEventHandlers } from "@rfice/eventhandler";
import { Configure } from "@/frameworks/libraries/utilities";
import { useBeforeUnload } from "@/frameworks/libraries/hooks";

import { useChatManagerHandler } from "./private";
import { EventID, Topics } from "@/frameworks/core/commons";
import { useChatRoomListActions } from "../useChatRoomList";
import { useMinimizeChatList, useRoomViewActions } from "@/frameworks/core/hooks";

/**
 *
 * @description 채팅의 공통 로직을 관리하는 훅입니다.
 */
export default function useChatServiceManager() {
	const chatRoomListActions = useChatRoomListActions();
	const { minimizeChatListActions } = useMinimizeChatList();
	const { roomViewCloseAll } = useRoomViewActions();

	const {
		roomCreated,
		groupDelete,
		memberLeft,
		memberNotificationUpdated,
		memberAdded,
		roomDeleted,
		messageSent,
		voiceCallStatusUpdated,
		roomNameUpdated,
		messageRead,
		messageReadAll,
		memberRoomPinned,
		memberRoomHided,
		roomNoticeNotified,
		roomNoticeUpdate,
		userUpdated,
		userBlock,
		groupWithdrawn,
		spaceInfoUpdated,
		myRoomUpdated,
		addSpaceChannel,
		deleteSpaceChannel,
		roomViewUpdated,
		roomOwnerChanged
	} = useChatManagerHandler();

	const run = () => Promise.all([chatRoomListActions.load("DM"), chatRoomListActions.load("Space")]);
	const stompNetwork = useStompManager();

	useStompClient(
		{
			onMessageReceived(message) {
				// console.debug("#useChatServiceManager# onMessageReceived", message);
				const messageType = message.type as ChatServices.MessageIDs | PushServices.User.MessageIDs;
				if (messageType === "typing") {
					return;
				}

				switch (messageType) {
					case "messageSent": {
						messageSent(message.payload as ChatServices.Server.MessageSent.Data);
						break;
					}
					case "roomCreated": {
						roomCreated(message.payload as ChatServices.Server.RoomCreated.Data);
						if (!stompNetwork.hasTopics([Topics.Room(message.payload.roomInfo.roomId)])) {
							stompNetwork.subscribe(Topics.Room(message.payload.roomInfo.roomId));
						}
						break;
					}
					case "memberAdded": {
						if (message.payload.roomInfo.roomType === "OPEN_WORLD") return;
						memberAdded(message.payload as ChatServices.Server.MemberAdded.Data);
						break;
					}
					case "roomDeleted": {
						roomDeleted(message.payload as ChatServices.Server.RoomDeleted.Data);
						break;
					}
					case "memberLeft": {
						// 해당 스페이스 나가기시 나간 본인만 처리하면 됨. ( space chat list 에서 제거 )
						if (message.payload.leftMemberId === Configure.userID) {
							memberLeft(message.payload as ChatServices.Server.MemberLeft.Data);
						}

						break;
					}
					case "memberNotificationUpdated": {
						memberNotificationUpdated(
							message.payload as ChatServices.Server.MemberNotificationUpdated.Data
						);
						break;
					}
					case "voiceChatStatusUpdated": {
						voiceCallStatusUpdated(message.payload as ChatServices.Server.VoiceCallStatusUpdated.Data);
						break;
					}
					case "roomNameUpdated": {
						roomNameUpdated(message.payload as ChatServices.Server.RoomNameUpdated.Data);
						break;
					}
					case "messageRead": {
						// 관리되는 주체가 달라서 합치지 않고 각 핸들러의 Read 함수 호출
						messageRead(message.payload as ChatServices.Server.MessageRead.Data);
						minimizeChatListActions.messageRead(message.payload?.roomId as string);
						break;
					}
					case "messageReadAll": {
						messageReadAll();
						minimizeChatListActions.messageReadAll();
						break;
					}
					case "memberRoomPinned": {
						memberRoomPinned(message.payload as ChatServices.Server.MemberRoomPinned.Data);
						break;
					}
					case "memberRoomHided": {
						memberRoomHided(message.payload as ChatServices.Server.MemberRoomHided.Data);
						break;
					}
					case "roomNoticeNotified": {
						roomNoticeNotified(message.payload as ChatServices.Server.RoomNoticeNotified.Data);
						break;
					}
					case "messageUpdated": {
						// 투표선택 관련된 message는 roomNoticeNotified 가 아닌 messageUpdated로 오기 때문에 vote 일때만 예외로 처리함.
						const { message: msg } = message.payload as ChatServices.Server.MessageUpdated.Data;
						if (JSON.parse(msg).type === "vote") {
							roomNoticeUpdate(message.payload as ChatServices.Server.MessageUpdated.Data);
						}

						break;
					}
					case "userUpdated": {
						userUpdated(message.payload as PushServices.User.UserUpdated.Data);
						break;
					}
					case "userBlock": {
						userBlock(message.payload as PushServices.User.UserBlock.Data);
						break;
					}
					case "roomViewed": {
						roomViewUpdated(message.payload as ChatServices.Server.RoomViewed.Data);
						break;
					}
					case "roomOwnerChanged": {
						roomOwnerChanged(message.payload as ChatServices.Server.RoomOwnerChanged.Data);
						break;
					}
				}
			}
		},
		[]
	);

	useStompClient({
		onMessageReceived(message) {
			const messageType = message.type as PushServices.Space.MessageIDs;
			switch (messageType) {
				case "spaceInfoUpdated": {
					const payload = message.payload as PushServices.Space.SpaceInfoUpdated.Data;
					spaceInfoUpdated(payload);
					break;
				}
				case "spaceCreated": {
					const payload = message.payload as PushServices.Space.SpaceCreated.Data;
					if (
						payload.createdSpaceInfo.createdBy === Configure.userID ||
						payload.createdSpaceInfo.accessAllowType === "PUBLIC"
					) {
						addSpaceChannel(payload.createdSpaceInfo.id);
					}
					break;
				}
				case "spaceDeleted": {
					const payload = message.payload as PushServices.Space.SpaceDeleted.Data;
					deleteSpaceChannel(payload.spaceInfo.id);
					break;
				}
				case "spaceInviteProcessed": {
					const payload = message.payload as PushServices.Space.SpaceInviteProcessed.Data;
					if (payload.userInfo.id === Configure.userID) {
						addSpaceChannel(payload.spaceInfo.id);
					}
					break;
				}
				case "myRoomUpdated": {
					const payload = message.payload as PushServices.Space.MyRoomUpdated.Data;
					myRoomUpdated(payload);
					break;
				}
			}
		}
	});

	useStompClient({
		onMessageReceived(message) {
			const messageType = message.type as PushServices.Group.MessageIDs;
			switch (messageType) {
				case "groupWithdrawn": {
					const payload = message.payload as PushServices.Group.GroupWithdrawn.Data;
					groupWithdrawn(payload);
					break;
				}
			}
		}
	});

	useEventHandlers(
		{
			// TODO: stomp 메시지 미준비로 FE쪽에서 작업
			[EventID.onGroupCommand]: (type: string, data: { groupID?: string; spaceIDs?: string[] }) => {
				switch (type) {
					case "delete":
						if (!data.groupID || !data.spaceIDs) return;
						groupDelete({ groupID: data.groupID, spaceIDs: data.spaceIDs });
						break;
					default: {
						break;
					}
				}
			}
		},
		[]
	);

	//
	useBeforeUnload(() => {
		roomViewCloseAll();
	}, []);

	return {
		run
	};
}
